<template>
  <div class="marquee-container" v-if="messages.length > 0">
    <div class="close-marquee" @click="closeMarquee" title="Close Marquee">&times;</div>
    <div class="marquee-content">
      <div class="marquee-track">
        <span v-for="(msg, index) in displayList" :key="index" class="marquee-item">
          <!-- Using v-html to render the message as it might contain HTML entities ❄️ -->
          <span v-html="msg"></span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
const marqueeStyles = `
.marquee-container {
  position: relative; /* Needed for positioning the close button */
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: #b22222; /* Holiday Red */
  color: white;
  overflow: hidden;
  white-space: nowrap;
  padding: 10px 0;
  z-index: 999;
  border-top: 2px solid gold;
}
.close-marquee {
  position: absolute;
  top: 1px;
  right: 15px;
  width: 30px;
  height: 30px;
  background: #b22222;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  z-index: 1000;
  transition: transform 0.2s ease, background-color 0.2s ease;
  box-shadow: 0 2px 5px rgba(0,0,0,0.3);
}
.marquee-content {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
}
.marquee-track {
  display: inline-block;
  animation: scroll linear infinite;
}
.marquee-item {
  display: inline-block;
  padding: 0 25px; /* Use padding for spacing instead of margin */
  font-weight: bold;
  font-size: 1.2rem;
}
@keyframes scroll {
  0% { transform: translateX(0); }
  100% { transform: translateX(-33.33%); }
}
.marquee-container:hover .marquee-track {
  animation-play-state: paused;
}
.close-marquee:hover {
  transform: scale(1.1);
  background-color: #c93434;
}`;
 /* Dureation of teh scroll, the shortr, the faster */
export default {
  data() {
    return {
      messages: [],
      polling: null,
      observer: null
    };
  },
  computed: {
    // Duplicating the list helps fill the screen for the animation
    displayList() {
      // Add a snowflake separator between messages for better visual distinction
      const separator = '❄️';
      const repeatedMessages = [...this.messages, ...this.messages, ...this.messages];
      return repeatedMessages.map(msg => `${separator} ${msg}`);
    }
  },
  watch: {
    // Watch for the messages array to be populated for the first time.
    messages(newMessages, oldMessages) {
      // We only want to run this setup once, when the messages go from empty to populated.
      if (newMessages.length > 0 && oldMessages.length === 0) {
        this.$nextTick(() => {
          // Now that `v-if` is true, this.$el is a real element.
          if (this.$el && !this.observer) {
            this.observer = new IntersectionObserver((entries) => {
              entries.forEach(entry => {
                if (entry.isIntersecting) {
                  this.updateAnimationSpeed();
                }
              });
            });
            this.observer.observe(this.$el);
          }
        });
      }
    }
  },
  mounted() {
    // Manually inject styles into the shadow DOM
    // This ensures styles are applied regardless of the build process
    const styleTag = document.createElement('style');
    styleTag.innerHTML = marqueeStyles;
    const shadowRoot = this.$el.getRootNode();
    if (shadowRoot) {
      shadowRoot.appendChild(styleTag);
    }

    window.addEventListener('resize', this.updateAnimationSpeed);
    this.fetchMessages();
    // Poll for updates every 10 seconds : 10000
    const messageFetchFrequency = 100000;  // In milliseconds
    this.polling = setInterval(this.fetchMessages, messageFetchFrequency);
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.updateAnimationSpeed);
    clearInterval(this.polling);
  },
  methods: {
    async fetchMessages() {
      const hostElement = this.$el.getRootNode().host;

      return; // Return empty for now. 
      // Only fetch if the marquee is visible (has the 'active' class)
      // or if there are no messages loaded yet.
      if (this.messages.length > 0 && (!hostElement || !hostElement.classList.contains('active'))) {
        // console.log("Marquee not active, skipping fetch.");
        return;
      }

      try {
        // console.log(`Fetching messages at: ${new Date().toLocaleString()}`);
        const res = await fetch('/marquee-messages');
        const data = await res.json();
        // Only update and recalculate if the messages have changed
        const newMessages = data.map(item => item.message);
        if (JSON.stringify(this.messages) !== JSON.stringify(newMessages)) {
          this.messages = data;
          this.updateAnimationSpeed();
        }
      } catch (error) {
        console.error("Failed to load holiday cheer:", error);
      }
    },
    closeMarquee() {
      const hostElement = this.$el.getRootNode().host;
      if (hostElement) {
        hostElement.classList.remove('active');
      }
    },
    updateAnimationSpeed() {
      this.$nextTick(() => {
        const track = this.$el.querySelector('.marquee-track');
        if (!track) return;

        // If the element is not visible, its width will be 0.
        // The IntersectionObserver handles re-triggering this when it becomes visible.
        if (this.$el.offsetWidth === 0) return;

        const trackWidth = track.offsetWidth;
        // Define a comfortable reading speed, e.g., 80 pixels per second
        const pixelsPerSecond = 400;
        const duration = trackWidth / pixelsPerSecond;

        // Set the new animation duration, removing the fixed duration from the CSS
        track.style.animationDuration = `${duration}s`;
      });
    }
  }
};
</script>